/**
 * @file Layout des pages publiques d'un établissement (Header + contenu + Footer).
 * @module layouts.public.PublicLayout
 *
 * Route layout unique du site public : possède le squelette pleine hauteur
 * (CssBaseline, Header sticky, zone de contenu flexible, Footer) que les pages
 * n'ont plus à composer — elles ne rendent que leur contenu dans l'Outlet.
 * Même doctrine que `layouts/app` côté dashboard.
 *
 * Les détails de l'établissement sont chargés ici par slug ; les pages qui
 * appellent aussi `useGetCompanyDetails` partagent la même entrée de cache SWR
 * (aucune requête supplémentaire). Les besoins spécifiques d'une page (props
 * de navigation du Header pour l'accueil, fond de page) remontent via
 * `public-page-context`, jamais par recensement des routes ici.
 */
import React from 'react';
import {Outlet, useParams} from 'react-router-dom';
import {Box, CssBaseline} from '@mui/material';
import {useGetCompanyDetails} from 'packages/common/hooks/api/public/Company';
import Header from './Header';
import Footer from './Footer';
import {PublicPageProvider, usePublicPageValues} from './public-page-context';

/**
 * Squelette du layout : lit les déclarations de la page courante et compose
 * Header, contenu et Footer.
 *
 * @return {JSX.Element}
 */
function PublicLayoutShell() {
  const {slug} = useParams();
  const {companyDetails} = useGetCompanyDetails(slug);
  const {headerProps, background, bottomInset, headerRef} = usePublicPageValues();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        ...(background && {bgcolor: background}),
        // Espace sous le Footer réclamé par les pages à barre sticky mobile
        ...(bottomInset && {paddingBottom: bottomInset}),
      }}
    >
      <CssBaseline />

      <Header ref={headerRef} companyDetails={companyDetails} {...(headerProps || {})} />

      {/* Colonne flexible : les contenus qui déclarent flex:1 (vues centrées
          verticalement) s'étirent, le Footer reste collé en bas de fenêtre */}
      <Box component="main" sx={{flex: 1, display: 'flex', flexDirection: 'column'}}>
        <Outlet />
      </Box>

      <Footer companyDetails={companyDetails} />
    </Box>
  );
}

/**
 * Route layout du site public : fournit le contexte de déclarations de page
 * puis rend le squelette.
 *
 * @return {JSX.Element}
 */
export default function PublicLayout() {
  return (
    <PublicPageProvider>
      <PublicLayoutShell />
    </PublicPageProvider>
  );
}
